import { ErrorMessage } from '@components/Form'
import { BoundaryRequestKind, useGenerateBoundary } from '@features/fields/api/useGenerateBoundary'
import { useGeneratePrescription } from '@features/fields/api/useGeneratePrescription'
import type { GuidanceDraft } from '@features/fields/api/useSaveGuidancePlan'
import type { ZoneSource } from '@features/fields/api/useZoneTuning'
import { useAnswerBoundaryAutogen } from '@features/fields/hooks/useAnswerBoundaryAutogen'
import { AutoBoundaryStatus, useBoundaryAutogen } from '@features/fields/hooks/useBoundaryAutogen'
import type { BoundaryAutogenRequest } from '@features/fields/lib/boundaryAutogenRequest'
import type { PrescriptionProduct } from 'api-validations/aurora'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { ControllableApplication } from 'api-validations/stitch'
import { useState } from 'react'
import {
  BoundaryIcon,
  ButtonKind,
  CircleSpinner,
  type DisabledGuard,
  Icon,
  Loading,
  PanoramaButton,
  PrescriptionIcon
} from 'shared-ui/components'
import { useCffForPlanningField } from 'shared-ui/field-data'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { useAsAppliedLayerOrNull, useMapContext } from 'shared-ui/map'
import { isNonNil } from 'utils'

import { AutoGenerateOptions } from './AutoGenerateOptions'
import { BoundaryDrawing } from './BoundaryDrawing'
import { BoundarySourcePicker } from './BoundarySourcePicker'
import { GuidanceDrawing } from './GuidanceDrawing/GuidanceDrawing'
import type { Zone } from './PrescriptionDetailsTab'
import { PrescriptionDrawing } from './PrescriptionDrawing'
import { ZoneTuning } from './ZoneTuning'

export enum AutoGenerateSelection {
  Auto = 'auto',
  Manual = 'manual',
  Unselected = 'unselected'
}
export type CreationChoices = {
  type: Mode
  boundaryId?: string
  auto: AutoGenerateSelection
}

export enum Mode {
  Guidance = 'Guidance',
  Boundary = 'Boundary',
  Prescription = 'Prescription',
  Plan = 'plan'
}
export const BoundaryOptions = ({
  cancel,
  fieldId,
  mode,
  boundaryId: BId,
  processSavedAssets,
  onGuidancePlanBuilt,
  initialGuidance,
  autogenRequest = null,
  initialAuto = AutoGenerateSelection.Unselected
}: {
  boundaryId?: string
  cancel: () => void
  fieldId: string
  mode: Mode.Boundary | Mode.Prescription | Mode.Guidance
  processSavedAssets: (
    assets: {
      id: string
      name: string
      type?: ControllableApplication | GuidancePrimitive
    }[]
  ) => void
  onGuidancePlanBuilt?: (draft: GuidanceDraft) => void
  initialGuidance?: GuidanceDraft
  autogenRequest?: BoundaryAutogenRequest | null
  initialAuto?: AutoGenerateSelection
}) => {
  const { t } = useLocalization()

  const [boundaryId, setBoundaryId] = useState(BId)

  // Guidance skips the chooser screen entirely — the wizard is the flow, and
  // it loads the plan's boundary itself.
  const [creationChoices, setCreationChoices] = useState<CreationChoices>({
    type: mode,
    boundaryId,
    auto: mode === Mode.Guidance ? AutoGenerateSelection.Auto : initialAuto
  })

  const generatePrescription = useGeneratePrescription()
  const [zoneSource, setZoneSource] = useState<ZoneSource>()
  const [tuned, setTuned] = useState<{ zones: Zone[]; products: PrescriptionProduct[] }>()

  // A boundary can be outlined from the field's own passes; a prescription still needs a
  // selected as-applied layer to take its rates from, unless the planner picks one here.
  const { map } = useMapContext()
  const noAsAppliedLayer = useAsAppliedLayerOrNull(map.isComparison()) === null
  const picksOwnLayer = mode === Mode.Prescription && noAsAppliedLayer
  const ownLayerSource = picksOwnLayer ? zoneSource : undefined
  const generateBoundary = useGenerateBoundary()
  const { autoBoundary, sources } = useBoundaryAutogen(fieldId)
  const cff = useCffForPlanningField(fieldId)

  useAnswerBoundaryAutogen({
    request: autogenRequest,
    sources,
    generate: ({ source, onGenerated, onError }) => {
      setCreationChoices((choices) => ({ ...choices, auto: AutoGenerateSelection.Auto }))
      generateBoundary.mutate(
        { kind: BoundaryRequestKind.Passes, source },
        { onSuccess: onGenerated, onError }
      )
    }
  })

  if (generatePrescription.isPending || generateBoundary.isPending) {
    return <Loading />
  }

  if (generateBoundary.isError || generatePrescription.isError) {
    return (
      <Container mode={mode}>
        <ErrorMessage className="m-4">
          {generateBoundary.error?.message ??
            generatePrescription.error?.message ??
            t(($) => $.messages.errorOccurred)}
        </ErrorMessage>
        <BottomButtons onCancel={cancel} />
      </Container>
    )
  }

  if (creationChoices.auto === AutoGenerateSelection.Unselected) {
    return (
      <AutoGenerateOptions
        setBoundaryId={setBoundaryId}
        onAutoSelection={() => {
          if (mode === Mode.Guidance) return
          if (isNonNil(ownLayerSource)) return
          if (mode === Mode.Prescription && isNonNil(creationChoices.boundaryId)) {
            generatePrescription.mutate(creationChoices.boundaryId)
          } else if (autoBoundary.status === AutoBoundaryStatus.Ready) {
            generateBoundary.mutate(autoBoundary.request)
          }
        }}
        onCancel={cancel}
        autoBoundaryStatus={autoBoundary.status}
        creationChoices={creationChoices}
        setCreationChoices={setCreationChoices}
        mode={mode}
        fieldId={fieldId}
        processSavedAssets={processSavedAssets}
        picksOwnLayer={picksOwnLayer}
        zoneSource={ownLayerSource}
        onZoneSourceChange={setZoneSource}
      />
    )
  }

  if (mode === Mode.Guidance) {
    return (
      <Container mode={mode}>
        <GuidanceDrawing
          fieldId={fieldId}
          returnToMain={cancel}
          boundaryId={boundaryId ?? ''}
          onGuidancePlanBuilt={onGuidancePlanBuilt}
          initialGuidance={initialGuidance}
        />
      </Container>
    )
  }

  if (
    creationChoices.type === Mode.Prescription &&
    creationChoices.auto === AutoGenerateSelection.Auto &&
    picksOwnLayer &&
    isNonNil(creationChoices.boundaryId) &&
    tuned === undefined
  ) {
    return (
      <Container mode={mode}>
        <ZoneTuning
          fieldId={fieldId}
          boundaryId={creationChoices.boundaryId}
          initialSource={ownLayerSource}
          onAccept={(zones, products) => setTuned({ zones, products })}
          onCancel={() => {
            setCreationChoices((choices) => ({
              ...choices,
              auto: AutoGenerateSelection.Unselected
            }))
          }}
        />
      </Container>
    )
  }

  if (creationChoices.type === Mode.Prescription) {
    return (
      <Container mode={mode}>
        <PrescriptionDrawing
          defaultZones={generatePrescription.data?.zones ?? tuned?.zones}
          defaultProducts={tuned?.products}
          fieldId={fieldId}
          returnToMain={(prescription) => {
            if (isNonNil(prescription)) {
              processSavedAssets(prescription)
              return
            }
            cancel()
          }}
        />
      </Container>
    )
  }

  const autoName =
    generateBoundary.isSuccess && cff !== undefined
      ? t(($) => $.planning.boundaryCreation.autoName, { field: cff.field })
      : undefined

  const generatedFrom =
    generateBoundary.variables?.kind === BoundaryRequestKind.Passes
      ? generateBoundary.variables.source
      : undefined

  return (
    <Container mode={mode}>
      {generateBoundary.isSuccess && (
        <div className="flex w-full flex-col gap-2 border-b border-gray-300 pb-4">
          {t(($) => $.planning.boundaryCreation.createdBoundary)}
          {generatedFrom !== undefined && (
            <BoundarySourcePicker
              sources={sources ?? []}
              selected={generatedFrom}
              onSelect={(source) => {
                generateBoundary.mutate({ kind: BoundaryRequestKind.Passes, source })
              }}
            />
          )}
        </div>
      )}
      <BoundaryDrawing
        fieldId={fieldId}
        defaultName={autoName}
        returnToMain={(boundary) => {
          if (isNonNil(boundary)) {
            processSavedAssets([boundary])
            return
          }
          cancel()
        }}
      />
    </Container>
  )
}

export const Container = ({
  children,
  mode
}: {
  mode: Mode
} & React.PropsWithChildren) => {
  const { t } = useLocalization()

  const configByMode = {
    [Mode.Prescription]: {
      title: 'createPrescription',
      icon: PrescriptionIcon
    },
    [Mode.Guidance]: {
      title: 'guidance',
      icon: ({ size, color }: { size: string; color: string }) => (
        <Icon icon={SpriteMap.GuidanceIcon} className={`${size} ${color}`} />
      )
    },
    [Mode.Boundary]: { title: 'createBoundary', icon: BoundaryIcon },
    [Mode.Plan]: { title: 'boundary', icon: BoundaryIcon }
  } as const

  return (
    <>
      <div className="pan-title3 flex items-center gap-2">
        {configByMode[mode].icon({ size: 'size-6', color: 'text-green-500' })}
        {t(($) => $.planning[configByMode[mode].title])}
      </div>
      {children}
    </>
  )
}

export const BottomButtons = ({
  onSave,
  onCancel,
  saveDisabledWhen,
  savePending,
  saveError
}: {
  onCancel: () => void
  onSave?: () => void
  saveDisabledWhen?: DisabledGuard | ReadonlyArray<DisabledGuard>
  savePending?: boolean
  saveError?: string
}) => {
  const { t } = useLocalization()

  return (
    <div className="flex h-fit w-full flex-col gap-3 border-t border-gray-300 py-5">
      <div className="flex w-full gap-3">
        <PanoramaButton
          type="button"
          kind={ButtonKind.Secondary}
          icon={SpriteMap.CloseCircleIcon}
          onClick={onCancel}
          className="w-full"
        >
          {t(($) => $.buttons.cancel)}
        </PanoramaButton>
        {onSave && (
          <PanoramaButton
            kind={isNonNil(saveError) ? ButtonKind.Danger : ButtonKind.Primary}
            disabledWhen={saveDisabledWhen}
            onClick={onSave}
            className="w-full"
            icon={SpriteMap.CheckCircleIcon}
          >
            {t(($) => $.buttons.save)}
            {(savePending ?? false) && <CircleSpinner />}
          </PanoramaButton>
        )}
      </div>
      {isNonNil(saveError) && <ErrorMessage>{saveError}</ErrorMessage>}
    </div>
  )
}
